import React, { useRef, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import PrintIcon from "@mui/icons-material/Print";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import api from "../../services/api";

// ─── Informações da loja ───────────────────────────────────────────────────────
const STORE = {
  name:   "KR Carimbos e Gráfica",
  address:"R. Álvares Maciel, 343 — Santa Efigênia — Belo Horizonte/MG",
  phone1: "(31) 3224-3820",
  phone2: "",
  email:  "carimbos@krcarimbos.com.br",
  cep:    "30150-250",
};
// ──────────────────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  aberta:      "Aberta",
  em_producao: "Pedido Confirmado",
  pronta:      "Pronta",
  entregue:    "Entregue",
  cancelada:   "Cancelada",
};

const fmtDate  = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
const fmtMoney = (v) => v != null ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";
const fmtPM    = (pm) => { if (!pm) return null; try { const p = JSON.parse(pm); return p.nome || p.descricao || pm; } catch { return pm; } };
const fmtDT    = (d)  => d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Estilos só usados na janela de impressão/PDF
const printStyles = `
  @page { size: A4 landscape; margin: 8mm 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #000; background: #fff; }

  .hdr { display:flex; justify-content:space-between; align-items:flex-start;
         border-bottom:2.5px solid #000; padding-bottom:8px; margin-bottom:11px; }
  .hdr-left { display:flex; align-items:flex-start; gap:14px; }
  .hdr-logo-img { height:52px; width:auto; object-fit:contain; }
  .hdr-logo  { font-size:26px; font-weight:900; letter-spacing:-1px; }
  .hdr-store { font-size:11.5px; color:#333; margin-top:4px; line-height:1.65; }
  .hdr-right { text-align:right; }
  .hdr-num   { font-size:28px; font-weight:900; font-family:monospace; }
  .hdr-emit  { font-size:11.5px; color:#333; margin-top:3px; }
  .badge-urg { display:inline-block; padding:2px 9px; border:1.5px solid #000;
               border-radius:10px; font-size:10px; font-weight:700; margin-top:4px; }

  .cols2 { display:grid; grid-template-columns:1fr 1fr; gap:0 28px; margin-bottom:11px; }
  .cols3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:4px 16px; }

  .sec-title { font-size:10px; font-weight:700; text-transform:uppercase;
               letter-spacing:.7px; color:#444; border-bottom:1px solid #aaa;
               padding-bottom:3px; margin-bottom:6px; }

  .lbl { font-size:10px; color:#555; margin-bottom:1px; }
  .val { font-size:14px; font-weight:600; }
  .val-lg { font-size:17px; font-weight:700; }

  table { width:100%; border-collapse:collapse; margin-bottom:11px; }
  th { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.5px;
       color:#444; border-top:1.5px solid #aaa; border-bottom:1.5px solid #aaa;
       padding:4px 7px; text-align:left; }
  td { font-size:13px; padding:4px 7px; border-bottom:1px solid #e5e5e5; }
  .tr { text-align:right; }
  .total td { font-weight:800; font-size:15px; border-top:2px solid #000;
              border-bottom:none; padding:5px 7px; }

  .obs { border:1px solid #bbb; border-radius:3px; padding:6px 9px;
         font-size:12px; white-space:pre-wrap; min-height:32px; margin-bottom:11px; }

  .sigs { display:flex; gap:28px; margin-top:16px; }
  .sig  { flex:1; border-top:1px solid #000; padding-top:5px;
          font-size:10px; color:#555; text-align:center; }

  .footer { border-top:1px solid #bbb; margin-top:10px; padding-top:5px;
            font-size:10px; color:#555; display:flex; justify-content:space-between; }

  .imgs { display:flex; gap:7px; flex-wrap:wrap; margin-bottom:11px; }
  .imgs img { width:80px; height:80px; object-fit:cover; border:1px solid #ccc; border-radius:3px; }

  /* Marca d'água diagonal */
  .watermark {
    position: fixed; top: 50%; left: 50%;
    transform: translate(-50%,-50%) rotate(-45deg);
    font-size: 96px; font-weight: 900; letter-spacing: 4px;
    opacity: 0.09; pointer-events: none; white-space: nowrap;
    z-index: 9999; text-transform: uppercase; font-family: Arial, sans-serif;
  }

  /* Tabela de parcelas */
  .inst-table th { background: #f5f5f5; }
  .inst-paid td { background: #f0fdf4; }
  .inst-due  td { background: #fff8f0; }
`;

const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

// Determina status de pagamento com base nas parcelas
const getPaymentStatus = (order) => {
  const installments = Array.isArray(order.installments) ? order.installments : [];
  if (installments.length === 0) return null;
  const totalPaid = installments.reduce((s, p) => s + (p.paidAt ? Number(p.amount) : 0), 0);
  let desconto = 0;
  try { const p = JSON.parse(order.paymentMethod || "{}"); desconto = Number(p?.desconto) || 0; } catch { /* free-text */ }
  const total = Math.max(0, (Number(order.total) || 0) - desconto);
  if (totalPaid <= 0) return "A RECEBER";
  if (totalPaid >= total) return "PAGO";
  return "PAGO PARCIAL";
};

const WATERMARK_STYLE = {
  "PAGO":        { text: "PAGO",         color: "#1b5e20" },
  "A RECEBER":   { text: "A RECEBER",    color: "#b71c1c" },
  "PAGO PARCIAL":{ text: "PAGO PARCIAL", color: "#e65100" },
};

// Etiqueta de envio 10x15 (impressora térmica)
const labelStyles = `
  @page { size: 100mm 150mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; color: #000; background: #fff; }
  .lbl-page { width: 100mm; height: 150mm; padding: 5mm; display: flex; flex-direction: column; }
  .lbl-hdr { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2mm; }
  .lbl-hdr img { height: 22mm; width: auto; max-width: 60mm; }
  .lbl-os { font-family: monospace; font-size: 17px; font-weight: 900; }
  .lbl-tag { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .8px; margin-bottom: 1.5mm; }
  .lbl-dest { min-height: 72mm; border: 2px solid #000; border-radius: 2mm; padding: 3mm; display: flex; flex-direction: column; }
  .lbl-obs { margin-top: 3mm; padding-top: 2mm; border-top: 1px solid #000; font-size: 14px; line-height: 1.35; white-space: pre-wrap; }
  .lbl-name { font-size: 20px; font-weight: 900; line-height: 1.15; margin-bottom: 2mm; }
  .lbl-phone { font-size: 15px; font-weight: 700; margin-bottom: 3mm; }
  .lbl-addr { font-size: 17px; line-height: 1.35; }
  .lbl-cep { font-size: 17px; margin-top: 2mm; }
  .lbl-rem { font-size: 14px; line-height: 1.4; border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 3mm; }
`;

const escHtml = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
const fmtCep  = (c) => { const d = String(c || "").replace(/\D/g, ""); return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : (c || ""); };

// Endereço de envio: o da OS (frete) tem prioridade; senão, o cadastro completo do Bling
export const getShippingAddress = (order, blingContact) => {
  if (order.freteEndereco?.trim()) return { linhas: [order.freteEndereco.trim()], cep: order.freteCep };
  const e = blingContact?.endereco;
  if (!e?.logradouro) return null;
  const rua = [e.logradouro, e.numero].filter(Boolean).join(", ") + (e.complemento ? ` — ${e.complemento}` : "");
  const cidade = [e.bairro, [e.municipio, e.uf].filter(Boolean).join("/")].filter(Boolean).join(" — ");
  return { linhas: [rua, cidade].filter(Boolean), cep: e.cep };
};

export const buildShippingLabelHtml = (order, blingContact) => {
  const addr = getShippingAddress(order, blingContact);
  if (!addr) return null;
  const name  = blingContact?.nome || order.clientName || order.contact?.name || "—";
  const phone = blingContact?.celular || blingContact?.telefone || order.clientPhone || order.contact?.number;
  return [
    "<!DOCTYPE html><html><head>",
    '<meta charset="utf-8"/>',
    `<title>Etiqueta OS ${escHtml(order.numero)}</title>`,
    `<style>${labelStyles}</style>`,
    '</head><body><div class="lbl-page">',
    '<div class="lbl-hdr">',
    `<img src="https://adm.graficakr.com.br/logo.png" alt="${escHtml(STORE.name)}"/>`,
    `<div class="lbl-os">OS ${escHtml(order.numero)}</div>`,
    "</div>",
    '<div class="lbl-rem">',
    '<div class="lbl-tag">Remetente</div>',
    `<strong>${escHtml(STORE.name)}</strong><br/>`,
    `${escHtml(STORE.address)}<br/>`,
    `CEP ${escHtml(STORE.cep)} · ${escHtml(STORE.phone1)}`,
    "</div>",
    '<div class="lbl-dest">',
    '<div class="lbl-tag">Destinatário</div>',
    `<div class="lbl-name">${escHtml(name)}</div>`,
    phone ? `<div class="lbl-phone">Tel: ${escHtml(phone)}</div>` : "",
    `<div class="lbl-addr">${addr.linhas.map(escHtml).join("<br/>")}</div>`,
    addr.cep ? `<div class="lbl-cep">CEP ${escHtml(fmtCep(addr.cep))}</div>` : "",
    order.freteObservacao?.trim() ? `<div class="lbl-obs"><strong>Obs:</strong> ${escHtml(order.freteObservacao.trim())}</div>` : "",
    "</div>",
    "</div></body></html>",
  ].join("");
};

// Etiqueta direto da lista: abre a janela já no clique (senão o navegador bloqueia
// o pop-up depois do await) e preenche quando o contato do Bling chegar.
export const printShippingLabel = async (order) => {
  const win = window.open("", "_blank");
  if (!win) return { ok: false, reason: "popup" };
  let blingContact = null;
  try {
    const { data } = await api.get(`/service-orders/${order.id}?include=blingContact`);
    blingContact = data.blingContact || null;
    order = { ...order, ...data };
  } catch { /* segue só com os dados da OS */ }
  const html = buildShippingLabelHtml(order, blingContact);
  if (!html) { win.close(); return { ok: false, reason: "sem_endereco" }; }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const img = win.document.querySelector("img");
  const go = () => setTimeout(() => win.print(), 200);
  if (img && !img.complete) { img.onload = go; img.onerror = go; } else go();
  return { ok: true };
};

const ATTACH_TYPE_LABEL = { arte: "Arte", crm: "CRM", comprovante: "Comprovante", outro: "Outros" };

const ServiceOrderPrint = ({ order, onClose }) => {
  const printRef = useRef();
  const [emissao] = useState(() => new Date());
  const [blingContact, setBlingContact] = useState(null);
  const [warnings, setWarnings] = useState({ missingFields: [], missingNcm: [] });
  const [loadingBling, setLoadingBling] = useState(false);
  const [includeAttachments, setIncludeAttachments] = useState(true);

  useEffect(() => {
    setLoadingBling(true);
    api.get(`/service-orders/${order.id}?include=blingContact`)
      .then(({ data }) => {
        if (data.blingContact) setBlingContact(data.blingContact);

        const missing = [];
        const bc = data.blingContact;
        if (bc) {
          if (!bc.cpf && !bc.cnpj) missing.push("CPF/CNPJ");
          // dataNascimento obrigatório só para pessoa física (CPF)
          if (bc.cpf && !bc.cnpj && !bc.dataNascimento) missing.push("Data de nascimento");
          const e = bc.endereco;
          if (!e?.logradouro || !e?.cep) missing.push("Endereço (logradouro e CEP)");
        }

        const items = Array.isArray(data.items) ? data.items : [];
        const missingNcm = items.filter(it => it.descricao && !it.ncm).map(it => it.descricao);

        setWarnings({ missingFields: missing, missingNcm });
      })
      .catch(() => {})
      .finally(() => setLoadingBling(false));
  }, [order.id]);

  const handlePrint = () => {
    const html = [
      "<!DOCTYPE html><html><head>",
      '<meta charset="utf-8"/>',
      `<title>OS ${order.numero}</title>`,
      `<style>${printStyles}</style>`,
      "</head><body>",
      printRef.current.innerHTML,
      "</body></html>",
    ].join("");
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, "_blank");
    if (win) win.addEventListener("load", () => setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 300));
    else URL.revokeObjectURL(url);
  };

  const handlePrintLabel = () => {
    const html = buildShippingLabelHtml(order, blingContact);
    if (!html) return;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, "_blank");
    if (win) win.addEventListener("load", () => setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 300));
    else URL.revokeObjectURL(url);
  };

  const clientName   = blingContact?.nome  || order.clientName  || order.contact?.name   || "—";
  const clientPhone  = blingContact?.celular || blingContact?.telefone || order.clientPhone || order.contact?.number || "—";
  const clientEmail  = blingContact?.email || null;
  // LGPD: CPF/CNPJ não devem aparecer completos no PDF — mascara o meio (mostra início e fim).
  const maskDoc = (doc) => {
    if (!doc) return doc;
    const d = String(doc).replace(/\D/g, "");
    if (d.length === 11) return `${d.slice(0, 3)}.•••.•••-${d.slice(9)}`;
    if (d.length === 14) return `${d.slice(0, 2)}.•••.•••/••••-${d.slice(12)}`;
    if (d.length > 5) return `${d.slice(0, 3)}${"•".repeat(d.length - 5)}${d.slice(-2)}`;
    return doc;
  };
  const clientDoc    = maskDoc(blingContact?.cpf || blingContact?.cnpj || null);
  const clientAddr   = blingContact?.endereco;
  const items        = Array.isArray(order.items) ? order.items.filter(it => it.descricao) : [];
  const installments = Array.isArray(order.installments) ? order.installments : [];
  const freteAtivo  = Boolean(order.freteAtivo);
  const freteValor  = freteAtivo ? (parseFloat(order.freteValor) || 0) : 0;
  const itemsSubtotal = items.reduce((s, it) => s + (parseFloat(it.valorTotal) || 0), 0);
  const grossTotal = order.total != null
    ? Number(order.total)
    : itemsSubtotal + freteValor;
  // Desconto (R$ fixo) embutido no paymentMethod JSON — abater do total bruto.
  let descontoVal = 0;
  try { const p = JSON.parse(order.paymentMethod || "{}"); descontoVal = Number(p?.desconto) || 0; } catch { /* free-text */ }
  const total = Math.max(0, grossTotal - descontoVal);

  const attachments = Array.isArray(order.attachments) ? order.attachments : [];
  const attachByType = attachments.reduce((acc, a) => {
    const t = a.type || "outro";
    if (!acc[t]) acc[t] = [];
    acc[t].push(a);
    return acc;
  }, {});

  const isImage = (url) => /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url || "");

  const paidAt = order.paidAt;
  const paymentStatus = paidAt ? "PAGO" : getPaymentStatus(order);
  const wm = paymentStatus ? WATERMARK_STYLE[paymentStatus] : null;
  const totalPaid = installments.reduce((s, p) => s + (p.paidAt ? Number(p.amount) : 0), 0);
  const totalDue  = total - totalPaid;

  // ── estilos inline (usados no preview do modal, refletem printStyles) ──
  const S = {
    hdr:       { display:"flex", justifyContent:"space-between", alignItems:"flex-start", borderBottom:"2.5px solid #000", paddingBottom:8, marginBottom:11 },
    hdrLogo:   { fontSize:28, fontWeight:900, letterSpacing:-1 },
    hdrStore:  { fontSize:13, color:"#444", marginTop:4, lineHeight:1.65 },
    hdrRight:  { textAlign:"right" },
    hdrNum:    { fontSize:30, fontWeight:900, fontFamily:"monospace" },
    hdrEmit:   { fontSize:13, color:"#333", marginTop:3 },
    badgeUrg:  { display:"inline-block", padding:"2px 9px", border:"1.5px solid #000", borderRadius:10, fontSize:11, fontWeight:700, marginTop:4 },
    cols2:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 28px", marginBottom:11 },
    cols3:     { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"4px 16px" },
    secTitle:  { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:.7, color:"#444", borderBottom:"1px solid #aaa", paddingBottom:3, marginBottom:6 },
    lbl:       { fontSize:11, color:"#555", marginBottom:1 },
    val:       { fontSize:15, fontWeight:600 },
    valLg:     { fontSize:18, fontWeight:700 },
    total:     { fontWeight:800, fontSize:16, borderTop:"2px solid #000", borderBottom:"none", padding:"5px 7px" },
    obs:       { border:"1px solid #bbb", borderRadius:3, padding:"6px 9px", fontSize:13, whiteSpace:"pre-wrap", minHeight:32, marginBottom:11 },
    sigs:      { display:"flex", gap:28, marginTop:16 },
    sig:       { flex:1, borderTop:"1px solid #000", paddingTop:5, fontSize:11, color:"#555", textAlign:"center" },
    footer:    { borderTop:"1px solid #bbb", marginTop:10, paddingTop:5, fontSize:11, color:"#555", display:"flex", justifyContent:"space-between" },
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <div style={{ padding:"20px 24px 0", background:"#fff", overflowX:"auto" }}>
        <div ref={printRef} style={{ minWidth:700 }}>

          {/* ── MARCA D'ÁGUA ── */}
          {wm && (
            <div style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%,-50%) rotate(-45deg)",
              fontSize: 100, fontWeight: 900, letterSpacing: 4,
              opacity: 0.09, pointerEvents: "none", whiteSpace: "nowrap",
              zIndex: 9999, textTransform: "uppercase",
              color: wm.color, userSelect: "none",
            }}>
              {wm.text}
            </div>
          )}

          {/* ── CABEÇALHO ── */}
          <div style={S.hdr}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
              <img
                src="https://adm.graficakr.com.br/logo.png"
                alt={STORE.name}
                style={{ height:52, width:"auto", objectFit:"contain", flexShrink:0 }}
              />
              <div>
                <div style={S.hdrLogo}>{STORE.name}</div>
                <div style={S.hdrStore}>
                  {STORE.address}<br/>
                  {STORE.phone1}{STORE.phone2 ? ` · ${STORE.phone2}` : ""} · {STORE.email}
                </div>
              </div>
            </div>
            <div style={S.hdrRight}>
              <div style={S.hdrNum}>{order.numero}</div>
              <div style={S.hdrEmit}>Emitido em: {fmtDT(emissao)}</div>
              {order.prioridade === "urgente" && <div style={S.badgeUrg}>▲ URGENTE</div>}
            </div>
          </div>

          {/* ── CLIENTE + DADOS DA OS (2 colunas) ── */}
          <div style={S.cols2}>
            {/* Coluna 1 — Cliente */}
            <div>
              <div style={S.secTitle}>Cliente</div>
              <div style={{ marginBottom:6 }}>
                <div style={S.lbl}>Nome</div>
                <div style={S.valLg}>{clientName}</div>
              </div>
              <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
                <div>
                  <div style={S.lbl}>Telefone</div>
                  <div style={S.val}>{clientPhone}</div>
                </div>
                {clientEmail && (
                  <div>
                    <div style={S.lbl}>E-mail</div>
                    <div style={S.val}>{clientEmail}</div>
                  </div>
                )}
                {clientDoc && (
                  <div>
                    <div style={S.lbl}>CPF/CNPJ</div>
                    <div style={S.val}>{clientDoc}</div>
                  </div>
                )}
                {order.blingOrderNumero && (
                  <div>
                    <div style={S.lbl}>Pedido Bling</div>
                    <div style={S.val}>#{order.blingOrderNumero}</div>
                  </div>
                )}
              </div>
              {clientAddr?.logradouro && (
                <div style={{ marginTop: 4 }}>
                  <div style={S.lbl}>Endereço</div>
                  <div style={{ ...S.val, fontSize: 13 }}>
                    {/* LGPD: não expõe número/complemento/CEP no PDF — só rua, bairro e cidade */}
                    {clientAddr.logradouro}
                    {clientAddr.bairro ? `, ${clientAddr.bairro}` : ""}
                    {clientAddr.municipio ? ` — ${clientAddr.municipio}` : ""}
                    {clientAddr.uf ? `/${clientAddr.uf}` : ""}
                  </div>
                </div>
              )}
              {order.billingName && (
                <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px dashed #ddd" }}>
                  <div style={S.lbl}>Faturado para (NF)</div>
                  <div style={S.val}>
                    {order.billingName}
                    {order.billingDocument ? ` — ${maskDoc(order.billingDocument)}` : ""}
                  </div>
                </div>
              )}
            </div>

            {/* Coluna 2 — Dados da OS */}
            <div>
              <div style={S.secTitle}>Dados da OS</div>
              <div style={S.cols3}>
                <div>
                  <div style={S.lbl}>Prazo de produção</div>
                  <div style={S.val}>{fmtDate(order.deadline)}</div>
                </div>
                <div>
                  <div style={S.lbl}>Status</div>
                  <div style={S.val}>{STATUS_LABELS[order.status] || order.status}</div>
                </div>
                {fmtPM(order.paymentMethod) && (
                  <div>
                    <div style={S.lbl}>Forma de pagamento</div>
                    <div style={S.val}>{fmtPM(order.paymentMethod)}</div>
                  </div>
                )}
                {order.responsible?.name && (
                  <div>
                    <div style={S.lbl}>Responsável</div>
                    <div style={S.val}>{order.responsible.name}</div>
                  </div>
                )}
                <div>
                  <div style={S.lbl}>Arquivo aprovado</div>
                  <div style={S.val}>{order.fileApproved ? "✓ Sim" : "✗ Não"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── ITENS DO SERVIÇO ── */}
          {items.length > 0 && (
            <>
              <div style={S.secTitle}>Itens do serviço</div>
              <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:11 }}>
                <thead>
                  <tr>
                    {[["Descrição","left",""],["Qtd","right","52px"],["V. Unit.","right","95px"],["Total","right","105px"]].map(([h,a,w]) => (
                      <th key={h} style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.5, color:"#444", borderTop:"1.5px solid #aaa", borderBottom:"1.5px solid #aaa", padding:"4px 7px", textAlign:a, width:w||undefined }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5" }}>{it.descricao}</td>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{it.qtd}</td>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{fmtMoney(it.valorUnit)}</td>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{fmtMoney(it.valorTotal)}</td>
                    </tr>
                  ))}
                  {freteAtivo && freteValor > 0 && (
                    <tr>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", color:"#555", fontStyle:"italic" }}>
                        Frete{order.freteEndereco ? ` — ${order.freteEndereco}` : ""}{order.freteCep ? ` (CEP ${order.freteCep})` : ""}
                      </td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>—</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>—</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right", fontWeight:600 }}>{fmtMoney(freteValor)}</td>
                    </tr>
                  )}
                  {descontoVal > 0 && (
                    <>
                      <tr>
                        <td colSpan={3} style={{ fontSize:13, padding:"3px 7px", textAlign:"right", color:"#555" }}>Subtotal</td>
                        <td style={{ fontSize:13, padding:"3px 7px", textAlign:"right" }}>{fmtMoney(grossTotal)}</td>
                      </tr>
                      <tr>
                        <td colSpan={3} style={{ fontSize:13, padding:"3px 7px", textAlign:"right", color:"#0a7d3e", fontWeight:600 }}>Desconto</td>
                        <td style={{ fontSize:13, padding:"3px 7px", textAlign:"right", color:"#0a7d3e", fontWeight:600 }}>-{fmtMoney(descontoVal)}</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td colSpan={3} style={{ ...S.total, textAlign:"right" }}>TOTAL</td>
                    <td style={{ ...S.total, textAlign:"right" }}>{fmtMoney(total)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* ── PARCELAS ── */}
          {installments.length > 0 && (
            <>
              <div style={S.secTitle}>Controle de recebimento</div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 11 }}>
                <thead>
                  <tr>
                    {[["Parcela","left",""],["Valor","right","110px"],["Vencimento","right","110px"],["Situação","right","110px"]].map(([h,a,w]) => (
                      <th key={h} style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.5, color:"#444", borderTop:"1.5px solid #aaa", borderBottom:"1.5px solid #aaa", padding:"4px 7px", textAlign:a, width:w||undefined }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {installments.map((p, i) => (
                    <tr key={i} style={{ background: p.paidAt ? "#f0fdf4" : "#fff8f0" }}>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5" }}>{p.label || `Parcela ${i+1}`}</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right", fontWeight:600 }}>{fmtMoney(p.amount)}</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{p.dueDate ? fmtDateShort(p.dueDate + "T12:00:00") : "—"}</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right", color: p.paidAt ? "#388e3c" : "#d32f2f", fontWeight:700 }}>
                        {p.paidAt ? `✓ Recebido ${fmtDateShort(p.paidAt)}` : "⏳ A receber"}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={2} style={{ ...S.total, textAlign:"right" }}>
                      <span style={{ color:"#388e3c" }}>Recebido: {fmtMoney(totalPaid)}</span>
                      <span style={{ marginLeft:16, color: totalDue > 0 ? "#d32f2f" : "#388e3c" }}>
                        A receber: {fmtMoney(Math.max(0, totalDue))}
                      </span>
                    </td>
                    <td colSpan={2} style={{ ...S.total, textAlign:"right" }}>TOTAL: {fmtMoney(total)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* ── OBSERVAÇÕES ── */}
          {order.observations && (
            <>
              <div style={S.secTitle}>Observações</div>
              <div style={S.obs}>{order.observations}</div>
            </>
          )}

          {/* ── ANEXOS (agrupados por tipo) ── */}
          {includeAttachments && attachments.length > 0 && (
            <>
              {Object.entries(attachByType).map(([type, list]) => (
                <React.Fragment key={type}>
                  <div style={S.secTitle}>{ATTACH_TYPE_LABEL[type] || type}</div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:11 }}>
                    {list.map((a, i) => (
                      isImage(a.url) ? (
                        <img key={i} src={a.url} alt={a.name}
                          style={{ maxWidth:"100%", maxHeight:200, objectFit:"contain", border:"1px solid #ccc", borderRadius:3 }} />
                      ) : (
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:6, background:"#f5f5f5", border:"1px solid #ccc", borderRadius:4, padding:"4px 10px", fontSize:12 }}>
                          <span>📄</span><span>{a.name}</span>
                        </div>
                      )
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </>
          )}

          {/* ── ASSINATURAS ── */}
          <div style={S.sigs}>
            {["Recebido por / Data", "Responsável pela produção", "Aprovação do cliente"].map(label => (
              <div key={label} style={S.sig}>{label}</div>
            ))}
          </div>

          {/* ── RODAPÉ ── */}
          <div style={S.footer}>
            <span>{STORE.name} — {STORE.address}</span>
            <span>{STORE.phone1}{STORE.phone2 ? ` · ${STORE.phone2}` : ""} · {STORE.email}</span>
          </div>

        </div>
      </div>

      <DialogActions style={{ padding:"10px 24px", flexDirection:"column", alignItems:"stretch", gap:6 }}>
        {loadingBling && (
          <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#888" }}>
            <CircularProgress size={12} /> Buscando dados atualizados do Bling...
          </div>
        )}
        {warnings.missingFields.length > 0 && (
          <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"#fff8e1", border:"1px solid #ffe082", borderRadius:6, padding:"7px 12px", fontSize:12 }}>
            <WarningAmberIcon style={{ fontSize:16, color:"#f57c00", marginTop:1, flexShrink:0 }} />
            <span>
              <strong>Cadastro incompleto no Bling:</strong> faltam {warnings.missingFields.join(", ")}
              {order.contact?.blingContactId && (
                <a href={`https://www.bling.com.br/contatos.php#edit/${order.contact.blingContactId}`} target="_blank" rel="noreferrer"
                  style={{ marginLeft:8, color:"#1565c0", textDecoration:"underline" }}>
                  Ver no Bling ↗
                </a>
              )}
            </span>
          </div>
        )}
        {warnings.missingNcm.length > 0 && (
          <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"#fff8e1", border:"1px solid #ffe082", borderRadius:6, padding:"7px 12px", fontSize:12 }}>
            <WarningAmberIcon style={{ fontSize:16, color:"#f57c00", marginTop:1, flexShrink:0 }} />
            <span><strong>Itens sem NCM:</strong> {warnings.missingNcm.join(", ")}</span>
          </div>
        )}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          {attachments.length > 0 && (
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer", color:"#555" }}>
              <input type="checkbox" checked={includeAttachments} onChange={e => setIncludeAttachments(e.target.checked)} />
              Incluir anexos na impressão ({attachments.length})
            </label>
          )}
          <div style={{ display:"flex", gap:8, marginLeft:"auto" }}>
            <Button onClick={onClose}>Fechar</Button>
            {(order.freteAtivo || order.freteEndereco) && (
              <Button variant="outlined" startIcon={<LocalShippingIcon />} onClick={handlePrintLabel}
                disabled={loadingBling || !getShippingAddress(order, blingContact)}
                title={getShippingAddress(order, blingContact) ? "Etiqueta 10x15 para a impressora térmica" : "OS sem endereço de entrega"}>
                Etiqueta de envio
              </Button>
            )}
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
              style={{ backgroundColor:"#333", color:"#fff" }}>
              Imprimir / PDF
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceOrderPrint;
